import React, { useEffect } from 'react';
import { Route, Switch, useParams } from 'react-router-dom';
import { prop, pick, path } from 'ramda';
import { usePrevious } from 'js/hooks/usePrevious';

import { Page } from 'js/components/system/Page';
import { getCleanPath, getURLParam } from 'js/utils/url.util';
import { componentService } from 'js/services/component.service';
import { PageInPopup } from 'js/components/system/Page/components/PageInPopup';
import { LegacyGameInfoPopup } from 'js/components/popups/LegacyGameInfoPopup';
import { GamesInfoPopupLauncher } from 'js/components/main/Games/GamesInfoPopup';
import { useAppSelector, usePageSelector, useUserSelector } from 'js/store/hooks';
import { QuickDepositPopupLauncher } from 'js/components/main/Cashier/QuickDepositPopup';
import { useUpdateUserLocationChat } from 'js/components/system/CommunicationWidgets/hooks';
import { BonusMultiviewBonusDetailsPopupLauncher } from 'js/components/popups/BonusMultiviewBonusDetailsPopup';

import { LaunchGame } from './components/LaunchGame';
import { useNavigateToPopupBackgroundPage, useNavigateToPrevPage } from './hooks';

export const PageRouter = () => {
    const { role } = useUserSelector(pick(['role']));
    const leaderboardPage = getCleanPath(useAppSelector(path(['leaderboard', 'page'])));
    const { rootPage, quickDepositPage, pageInPopupUrls, quickDepositUseCustom, sportsbookRootURL } = useAppSelector(
        pick(['rootPage', 'quickDepositPage', 'pageInPopupUrls', 'quickDepositUseCustom', 'sportsbookRootURL'])
    );
    const bonusPage = getCleanPath(useAppSelector(
        path(['bonusManagementPreferences', 'bonusDeepLinkingUrl'])
    ) || 'promotions');
    const { path: pageUrl = rootPage, pathLang } = useParams();
    const updateUserLocationChat = useUpdateUserLocationChat();

    const navigateToPrevPage = useNavigateToPrevPage();
    const navigateToPopupBackgroundPage = useNavigateToPopupBackgroundPage();
    const popupBackgroundPagePath = usePageSelector(prop('popupBackgroundPagePath'));

    let pagePath = pageUrl[0] === '/' ? pageUrl : `/${pageUrl}`;

    if (pagePath === '/' && pageInPopupUrls?.includes(rootPage)) {
        pageInPopupUrls.push('/');
    }

    pagePath = pagePath === '/' ? rootPage : pagePath;

    const parentPagePath = getCleanPath(popupBackgroundPagePath || rootPage);

    const prevPath = usePrevious(pagePath);

    useEffect(() => {
        if (pagePath !== prevPath) {
            updateUserLocationChat();
        }
    }, [pagePath, prevPath, updateUserLocationChat]);

    const pagesInPopup = pageInPopupUrls?.map((pageInPopupUrl) => `/:pathLang(${pathLang})?${pageInPopupUrl}`);
    const isPageAsPopUpDisabled = getURLParam(location.search, 'noPageAsPopup') === '1' || role === 'admin';

    return <Switch>
        <Route path={`/:pathLang(${pathLang})?/igaming/games-info/:gameCode`}>
            <Page path={parentPagePath} isPopupBackgroundPage={true} />
            <LegacyGameInfoPopup onClose={navigateToPopupBackgroundPage} />
        </Route>

        <Route path={`/:pathLang(${pathLang})?/igaming/:gameCode`}>
            <Page path={parentPagePath} isPopupBackgroundPage={true} />
            <LaunchGame />
        </Route>

        <Route path={`/:pathLang(${pathLang})?/online-game/:gameCode`}>
            <Page path={parentPagePath} isPopupBackgroundPage={true} />
            <GamesInfoPopupLauncher onClose={navigateToPrevPage} />
        </Route>

        <Route path={`/:pathLang(${pathLang})?/leaderboard-ingame/:leaderboardId`}>
            <Page path={pagePath} />
        </Route>

        <Route path={`/:pathLang(${pathLang})?/casino-leaderboard/:leaderboardId`}>
            <Page path={pagePath} />
        </Route>

        <Route path={`/:pathLang(${pathLang})?/mystery-parcel/:leaderboardId`}>
            <Page path={pagePath} />
        </Route>

        {
            quickDepositPage && !quickDepositUseCustom &&
            <Route path={`/:pathLang(${pathLang})?${quickDepositPage}`}>
                <Page path={parentPagePath} isPopupBackgroundPage={true} />
                <QuickDepositPopupLauncher />
            </Route>
        }

        {
            bonusPage &&
            <Route path={`/:pathLang(${pathLang})?${bonusPage}/:seoFriendlyBonusName`}>
                <Page path={parentPagePath} isPopupBackgroundPage={true} />
                <BonusMultiviewBonusDetailsPopupLauncher />
            </Route>
        }

        {
            leaderboardPage &&
            <Route path={`/:pathLang(${pathLang})?${leaderboardPage}/:leaderboardId`}>
                {
                    !isPageAsPopUpDisabled && pagesInPopup && pagesInPopup.includes(`/:pathLang(${pathLang})?${leaderboardPage}`) ? (
                        <>
                            <Page path={parentPagePath} isPopupBackgroundPage={true} />
                            <PageInPopup path={pagePath} />
                        </>
                    ) : (
                        <Page path={pagePath} />
                    )
                }
            </Route>
        }

        {
            sportsbookRootURL &&
            <Route path={`/:pathLang(${pathLang})?${sportsbookRootURL}/*`}>
                <Page path={sportsbookRootURL} />
            </Route>
        }

        {
            !isPageAsPopUpDisabled && pagesInPopup &&
            <Route exact path={pagesInPopup}>
                <Page path={parentPagePath} isPopupBackgroundPage={true} />
                <PageInPopup path={pagePath} />
            </Route>
        }

        <Route>
            <Page path={pagePath} />
        </Route>
    </Switch>;
};

componentService.registerWebContentComponents({ PageRouter });
